/**
 * Message kind determination utility
 * Determines the kind of message to send based on context and switch state
 */

export interface Message {
  id: string;
  content: string;
  is_user: boolean;
  created_at: string;
  kind?: "CHAT" | "ARTF" | "QUESTION" | "ANSWER" | "EDIT";
  model?: string;
  tokens?: number;
  is_edited?: boolean;
  is_regenerated?: boolean;
  retrieval_latency_ms?: number;
  model_latency_ms?: number;
  retrieval_hit_count?: number;
  parent_message_id?: string;
  client_message_id?: string | null;
}

/**
 * Determines the message kind based on the Create Artifact switch state and context
 * 
 * @param createArtifactSwitchOn - Whether the Create Artifact switch is ON
 * @param lastMessage - The last message in the conversation (if any)
 * @param editingArtifactId - The ID of the artifact being edited (if any)
 * @returns The message kind: "CHAT", "ARTF", "ANSWER", or "EDIT"
 * 
 * Rules:
 * - If editingArtifactId is provided, always return "EDIT"
 * - If switch is OFF, always return "CHAT"
 * - If switch is ON and last message is a QUESTION from assistant, return "ANSWER"
 * - If switch is ON otherwise, return "ARTF"
 */
export const determineMessageKind = (
  createArtifactSwitchOn: boolean,
  lastMessage: Message | null,
  editingArtifactId?: string | null
): "CHAT" | "ARTF" | "ANSWER" | "EDIT" => {
  // If editing an artifact, always return EDIT
  if (editingArtifactId) return "EDIT";
  
  if (!createArtifactSwitchOn) return "CHAT";
  
  // If responding to a QUESTION message with switch ON, send as ANSWER
  if (lastMessage?.kind === "QUESTION" && !lastMessage.is_user) {
    return "ANSWER";
  }
  
  // Otherwise, send as ARTF (artifact creation)
  return "ARTF";
};
